import React from 'react'
import clsx from 'clsx'
import uniqueId from 'lodash.uniqueid'
import { Progressbar as ElementStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import useDeprecatedComponent from '../../lib/hooks/useDeprecatedComponent'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import { CommonProps } from '../helperTypes'

type Props = {
  /** Specify the label for screen readers */
  'aria-label'?: string
  /**
   * The id that will be passed to the underlying element.
   */
  id?: string
  /** Useful when there is no progress value */
  indeterminate?: boolean
  /** Optional label */
  label?: string
  /**
   * Maximum value.
   * @default 100
   */
  max?: number
  /**
   * Minimum value.
   * @default 0
   */
  min?: number
  /** Callback when the progress animation is complete at 100% */
  onAnimationComplete?: () => void
  /** Optional position percentage text on the right */
  percentageRight?: boolean
  /** Optional percentage text */
  showPercentage?: boolean
  /** Colored status to guide users on the progress of the task */
  status?: 'success' | 'warning' | 'error'
  /**
   * The progress percentage value (from min to max).
   * @default 0
   */
  value?: number
}

type DeprecatedProps = {
  domain?: { min: number; max: number }
}

export type ProgressBarProps = Props & DeprecatedProps & CommonProps

const useStyles = createUseStyles(ElementStyles, 'progressbar')

const handleDeprecatedProps = (props: ProgressBarProps) => {
  const { domain, min, max, ...rest } = props
  return {
    min: (domain && domain.min) || min,
    max: (domain && domain.max) || max,
    ...rest,
  }
}

const ProgressBar = (props: ProgressBarProps) => {
  const {
    'aria-label': ariaLabel = 'progress',
    className,
    id: idProp,
    indeterminate = false,
    label,
    min = 0,
    max = 100,
    onAnimationComplete,
    percentageRight = false,
    showPercentage = false,
    status = 'default',
    style,
    value = 0,
    ...rest
  } = handleDeprecatedProps(props)
  useDeprecatedProps('ProgressBar', { domain: 'min/max' }, props)
  const classes = useStyles()
  const id: string = React.useMemo(() => idProp || uniqueId('ProgressBar-'), [idProp])

  const percent = Math.min(((value - min) / (max - min)) * 100, 100).toFixed(0)
  React.useEffect(() => {
    const complete = percent === '100'
    const timeout = setTimeout(() => {
      if (complete && onAnimationComplete) onAnimationComplete()
    }, 1000)

    return () => {
      clearTimeout(timeout)
    }
  }, [onAnimationComplete, percent])

  return (
    <>
      {label && (
        <div id={id} className={classes.label}>
          {label}
        </div>
      )}
      <div
        className={clsx(
          classes.root,
          {
            [classes._indeterminate]: indeterminate === true,
            [classes._success]: status === 'success',
            [classes._warning]: status === 'warning',
            [classes._error]: status === 'error',
          },
          className,
        )}
        style={style}
        role='progressbar'
        aria-valuenow={value}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-label={label ? undefined : ariaLabel}
        aria-labelledby={label ? id : undefined}
        {...rest}
      >
        {showPercentage && !percentageRight && <div className={classes.percentage}>{percent}%</div>}
        <div className={classes.track}>
          <div className={classes.fill} style={indeterminate ? undefined : { width: `${percent}%` }}></div>
        </div>
        {showPercentage && percentageRight && (
          <div className={clsx(classes.percentage, classes.percentage_right)}>{percent}%</div>
        )}
      </div>
    </>
  )
}

export default ProgressBar

// retro compatiblity
export const LinearProgress = (props: ProgressBarProps) => {
  useDeprecatedComponent('LinearProgress', 'Please use ProgressBar.')
  return <ProgressBar {...props} />
}
